import { format } from "date-fns";

import { APP_LOCALE } from "../misc/locale";
import type { DealStage } from "../types";
import { INBOX_STAGE } from "./stages";

/**
 * Resolves the label shown for a stored stage value. `dealStages` (Almikia
 * Epic EP, TASK-104) already carries every configured stage — system AND
 * direccion-created — so a direct lookup resolves a direccion-created stage
 * to its OWN configured label. Only a value not present in `dealStages` at
 * all (a legacy/unknown value, e.g. Atomic's "opportunity") falls back to
 * the inbox state's label (Prospecto). The stored value is never normalized
 * or written back — this is a display-only fallback.
 */
export const findDealLabel = (dealStages: DealStage[], dealValue: string) => {
  const directMatch = dealStages.find((stage) => stage.value === dealValue);
  if (directMatch) return directMatch.label;
  return dealStages.find((stage) => stage.value === INBOX_STAGE)?.label;
};

export function getRelativeTimeString(
  dateString: string,
  locale = APP_LOCALE,
): string {
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diff = date.getTime() - today.getTime();
  const unitDiff = Math.round(diff / (1000 * 60 * 60 * 24));

  // Check if the date is more than one week old
  if (Math.abs(unitDiff) > 7) {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
    }).format(date);
  }

  // Intl.RelativeTimeFormat for dates within the last week
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  return ucFirst(rtf.format(unitDiff, "day"));
}

function ucFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const isoDateStringRegex = /^\d{4}-\d{2}-\d{2}$/;

export function formatISODateString(dateString: string) {
  if (!isoDateStringRegex.test(dateString)) {
    throw new Error("Invalid date format. Expected YYYY-MM-DD.");
  }
  // Some browsers will consider a date in the format YYYY-MM-DD as UTC, which can cause off-by-one-day issues depending on the user's timezone.
  // To avoid this, we can parse the date components manually and create a date object in the local timezone.
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day);

  return format(date, "dd/MM/yyyy");
}
